/**
 * Transient backend-error classification.
 *
 * Two families recover on the next call without operator action:
 *  - Schema-cache reloads (`PGRST002` / "schema cache") — Supabase reloading
 *    PostgREST after a migration / grant change / restart.
 *  - Backend-unreachable / edge errors — the Kong/Envoy proxy in front of
 *    PostgREST cannot reach its upstream (database/PostgREST down, overloaded,
 *    pool exhausted, or a platform blip). The canonical live signature is the
 *    Envoy body "upstream connect error or disconnect/reset before headers.
 *    retried and the latest reset reason: connection timeout", plus Cloudflare
 *    5xx HTML pages and raw socket/connect failures.
 *
 * Classify by CATEGORY (HTTP status class, connect/timeout error codes, and a
 * curated message-shape list), NOT a single literal string — the edge emits
 * many wordings for the same "backend unreachable" condition, and a literal
 * allowlist silently fails to match the next variant. The 2026-06-07 incident:
 * the breaker never opened because "upstream connect error" matched nothing.
 *
 * Use to gate `logger.error` vs `logger.warn` and to drive circuit-breaker
 * `isFailure`: a poll loop that retries on its next interval drops these to
 * warn so a self-resolving outage doesn't fire the ERROR-rate alert, and the
 * breaker counts them so it can open and back the loop off.
 */

export function isTransientSchemaCacheError(err: unknown): boolean {
  if (!err || typeof err !== "object") {return false;}
  const e = err as { code?: unknown; message?: unknown };
  if (e.code === "PGRST002") {return true;}
  if (typeof e.message === "string" && /schema cache/i.test(e.message)) {
    return true;
  }
  return false;
}

// HTTP status codes that mean "backend unreachable/overloaded", never a
// business/validation outcome. Deliberately excludes 500 (a bare 500 is usually
// a real app bug) and all other 4xx (validation/auth — not blind-retry-safe).
const TRANSIENT_STATUS: ReadonlySet<number> = new Set([
  408, 425, 429, 502, 503, 504, 521, 522, 523, 524,
]);

// Node/undici/socket error codes for connect + DNS + reset failures.
const TRANSIENT_CODE =
  /^(ECONNREFUSED|ECONNRESET|ETIMEDOUT|EAI_AGAIN|EPIPE|ENETUNREACH|EHOSTUNREACH|UND_ERR_CONNECT_TIMEOUT|UND_ERR_SOCKET|UND_ERR_HEADERS_TIMEOUT)$/;

// Message shapes for the same "backend unreachable" condition across edges
// (Envoy/Kong, Cloudflare, undici/axios, supabase-js JSON-parse-of-HTML).
const TRANSIENT_MESSAGE: readonly RegExp[] = [
  /upstream connect error/i, // Envoy: cannot reach upstream
  /disconnect\/reset before headers/i, // Envoy reset
  /reset reason/i, // Envoy reset-reason suffix
  /no healthy upstream/i, // Envoy: all upstreams down
  /connect(?:ion)? timeout/i,
  /timeout of \d+ms exceeded/i, // axios/undici timeout
  /socket hang ?up/i,
  /network ?error/i,
  /fetch failed/i,
  /Unable to connect/i,
  // Node/undici surface the connect/DNS/reset code embedded in the message
  // (e.g. "connect ECONNREFUSED 127.0.0.1:443") and often WITHOUT a separate
  // `.code` field on the supabase-js error, so match them in message text too.
  /\b(?:ECONNREFUSED|ECONNRESET|ETIMEDOUT|EAI_AGAIN|EPIPE|ENETUNREACH|EHOSTUNREACH)\b/,
  /Unexpected token/i, // supabase-js JSON.parse of an HTML error body
  /<!DOCTYPE/i, // Cloudflare/proxy HTML error page
  /Bad ?gateway/i, // 502 text
  /Service Unavailable/i, // 503 text
  /Gateway Time-?out/i, // 504 text
];

function statusOf(e: { status?: unknown; statusCode?: unknown }): number | undefined {
  const s = e.status ?? e.statusCode;
  if (typeof s === "number") {return s;}
  if (typeof s === "string" && /^\d+$/.test(s)) {return Number(s);}
  return undefined;
}

function classifyOne(err: unknown): boolean {
  if (err === null || err === undefined) {return false;}
  if (typeof err === "string") {
    return TRANSIENT_MESSAGE.some((re) => re.test(err));
  }
  if (typeof err !== "object") {return false;}
  const e = err as {
    message?: unknown;
    code?: unknown;
    status?: unknown;
    statusCode?: unknown;
  };

  const status = statusOf(e);
  if (status !== undefined && TRANSIENT_STATUS.has(status)) {return true;}

  if (typeof e.code === "string" && TRANSIENT_CODE.test(e.code)) {return true;}

  if (
    typeof e.message === "string" &&
    TRANSIENT_MESSAGE.some((re) => re.test(e.message as string))
  ) {
    return true;
  }
  return false;
}

/**
 * True when `err` (or its `.cause`, one level deep) is a transient
 * backend-unreachable / edge-5xx / connect-timeout error that recovers on the
 * next call. False for ordinary PostgREST/business errors (unique violation,
 * FK violation, 4xx validation, etc.) — so the wrapper never masks a real bug.
 */
export function isTransientBackendError(err: unknown): boolean {
  if (classifyOne(err)) {return true;}
  if (err && typeof err === "object") {
    const cause = (err as { cause?: unknown }).cause;
    if (cause !== undefined && classifyOne(cause)) {return true;}
  }
  return false;
}

/**
 * @deprecated Back-compat alias for `isTransientBackendError`. Kept so existing
 * imports (`webhook-inbox.ts`) keep working unchanged.
 */
export const isTransientNetworkError = isTransientBackendError;
